import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react'

import {
  Modal,
  ModalProvider,
  SquareDotLoadingIndicator,
} from '@joor/design-system'
import styled from 'styled-components'

import {
  OrderBillingShippingDocument,
  OrderRequirementChecksDocument,
  OrderReviewGigaQueryDocument,
} from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'
import usePortal from 'hooks/usePortal'
import { useRenderToasters } from 'hooks/useRenderToasters'

import messages from '../../review.messages'
import { ProductsEditActions } from './components/ProductsEditActions'
import {
  PRODUCTS_PAGE_CONTENT_PARAM,
  SAVE_PRODUCTS_EVENT_NAME,
  SAVE_PRODUCTS_RESPONSE_EVENT_NAME,
  SAVING_LOADER_PRESENT_EVENT_NAME,
} from './productsEditModal.constants'
import { useProductsEditModal } from './productsEditModal.hooks'

export const ProductsEditModalIds = {
  productsEditIframe: 'products-edit-iframe',
  productsEditModal: 'products-edit-modal',
  productsEditModalClose: 'products-edit-modal-close',
}

export const ProductsEditModal = ({
  setIsProductsModalOpen,
  setIsAddProductsModalOpen,
  shouldShowReloadProducts,
  loading,
  url,
}: ProductsEditModalProps) => {
  const Portal = usePortal()
  const iRef = useRef<HTMLIFrameElement | null>(null)
  const shouldShowWarningRef = useRef<boolean>(true)
  const {
    loadingIframe,
    loadingSaveProducts,
    loadingSaveAndAddProducts,
    shouldShowAddProducts,
    updateData,
  } = useProductsEditModal()
  const { renderSuccessToast, renderErrorToast, renderWarningToast } =
    useRenderToasters()

  const handleSavedProductResponse = useCallback(
    (e: MessageEvent) => {
      // If we receive an event with the type SAVE_PRODUCTS_RESPONSE_EVENT_NAME, we need
      // to render the toasts and also refetch some queries to keep the tab updated
      if (e.data.type === SAVE_PRODUCTS_RESPONSE_EVENT_NAME) {
        if (e.data.success) {
          if (shouldShowAddProducts) {
            setIsAddProductsModalOpen(true)
          }
          setIsProductsModalOpen(false)
          renderSuccessToast({ description: messages.productsUpdated })
          atlasClient.refetchQueries({
            include: [
              OrderReviewGigaQueryDocument,
              OrderBillingShippingDocument,
              OrderRequirementChecksDocument,
            ],
          })
        } else {
          renderErrorToast({
            description: messages.errorWhileSaving,
          })
        }

        updateData({
          loadingSaveProducts: false,
          loadingSaveAndAddProducts: false,
          loadingIframe: false,
        })
      } else if (e.data.type === SAVING_LOADER_PRESENT_EVENT_NAME) {
        if (e.data.loader) {
          if (shouldShowAddProducts) {
            updateData({
              loadingSaveAndAddProducts: true,
            })
          } else {
            updateData({
              loadingSaveProducts: true,
            })
          }
          if (shouldShowWarningRef.current) {
            shouldShowWarningRef.current = false
            renderWarningToast({
              title: messages.productsSaving,
              description: messages.productsAreSaving,
              shouldCloseManually: true,
            })
          }
        }
      }
    },
    [shouldShowAddProducts],
  )

  const handleReloadIframe = () => {
    if (iRef.current) {
      updateData({ loadingIframe: true })
      iRef.current.src += ''
    }
  }

  useEffect(() => {
    window.addEventListener('message', handleSavedProductResponse, false)

    return () => {
      window.removeEventListener('message', handleSavedProductResponse)
    }
  }, [handleSavedProductResponse])

  const handleClickSave = (shouldOpenAddProductsModal?: boolean) => {
    updateData({
      loadingSaveAndAddProducts: !!shouldOpenAddProductsModal,
      shouldShowAddProducts: !!shouldOpenAddProductsModal,
    })
    iRef.current?.contentWindow?.postMessage(
      SAVE_PRODUCTS_EVENT_NAME,
      window.location.origin,
    )
  }

  const handleCloseModal = () => {
    shouldShowWarningRef.current = true
    shouldShowReloadProducts.current = false
    setIsProductsModalOpen(false)
  }

  const redirectUrlWithoutHeader = url
    ? `${window.location.origin}${url}&${PRODUCTS_PAGE_CONTENT_PARAM}=true`
    : ''

  return (
    <Portal>
      <ModalProvider>
        <StyledModal
          title={{ text: messages.editProducts, iconName: 'edit' }}
          isOpen
          onClose={handleCloseModal}
          disableBackgroundClick
          id={ProductsEditModalIds.productsEditModal}
          closeIconId={ProductsEditModalIds.productsEditModalClose}
        >
          <Modal.Content>
            {(loadingIframe || loading) && (
              <IframeLoaderWrapper>
                <SquareDotLoadingIndicator />
              </IframeLoaderWrapper>
            )}
            {url && (
              <StyledIframe
                title={messages.editProducts}
                src={redirectUrlWithoutHeader}
                onLoad={() => updateData({ loadingIframe: false })}
                ref={iRef}
                data-testid={ProductsEditModalIds.productsEditIframe}
              />
            )}
          </Modal.Content>
          <Modal.Actions>
            <ProductsEditActions
              loadingIframe={loadingIframe}
              loadingSaveProducts={loadingSaveProducts}
              loadingSaveAndAddProducts={loadingSaveAndAddProducts}
              shouldShowReloadProducts={shouldShowReloadProducts.current}
              handleClickSave={handleClickSave}
              handleCloseModal={handleCloseModal}
              handleReloadIframe={handleReloadIframe}
            />
          </Modal.Actions>
        </StyledModal>
      </ModalProvider>
    </Portal>
  )
}

export interface ProductsEditModalProps {
  setIsProductsModalOpen: Dispatch<SetStateAction<boolean>>
  setIsAddProductsModalOpen: Dispatch<SetStateAction<boolean>>
  shouldShowReloadProducts: MutableRefObject<boolean>
  url?: string
  loading?: boolean
}

const IframeLoaderWrapper = styled.div`
  position: absolute;
  width: calc(100% - 60px);
  height: 66vh;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledModal = styled(Modal)`
  overflow: visible;
  min-height: calc(100vh - 160px);
  min-width: 1088px;
`

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 317px);
  border: none;
`
